import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button } from '@mui/material';
import { useAtom } from 'jotai';
import { useMemo } from 'react';

import CheckPermissions from '@/components/CheckPermissions';
import { ContextMenuProvider } from '@/components/ContextMenu/ContextMenuProvider';
import { ExportButton } from '@/components/ExportButton';
import LoadingIndicator from '@/components/LoadingIndicator';
import { PERMISSIONS } from '@/constants/auth.constants';
import OrganizationEditDialog, {
  type OrganizationCreateRequest,
  useOrganizationDialog
} from '@/features/OrganizationEditDialog';
import OrganizationsTable from '@/features/OrganizationTable';
import ProductEditDialog, {
  useProductDialog
} from '@/features/ProductEditDialog';
import { EMPTY_PRODUCT } from '@/features/ProductEditDialog/ProductEditDialog.constants';
import SearchBar, { getSearchableSelectOptions } from '@/features/SearchBar';
import useConfirm from '@/hooks/useConfirm';
import Header from '@/layout/Header/Header';
import MainContent from '@/layout/MainContent';
import { isLayoutMobileAtom } from '@/store/layout';

import {
  ORGANIZATIONS_CONTEXT_MENU_ITEMS,
  ORGANIZATIONS_QUICK_FILTERS
} from './Organizations.constants';
import {
  filteredOrganizationsAtom,
  organizationsAtom,
  organizationsFiltersAtom,
  useFetchOrganizations,
  useSyncOrganization
} from './Organizations.store';
import useStyles from './Organizations.styles';
import {
  getProductSubTypeFilterOptions,
  getProductTypeFilterOptions
} from './Organizations.utils';

// avoid unnecessary re-renders
const OrgTable = () => <OrganizationsTable />;
OrgTable.displayName = 'OrgTable';

const Organizations = () => {
  const { classes } = useStyles();
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const [organizations] = useAtom(organizationsAtom);
  const [filteredOrganizations] = useAtom(filteredOrganizationsAtom);
  const { isFetching } = useFetchOrganizations();
  const [organizationsFilters, setOrganizationsFilters] = useAtom(
    organizationsFiltersAtom
  );
  const { openDialog, open, isSyncAllowed } = useOrganizationDialog();
  const syncOrganization = useSyncOrganization();
  const { ConfirmationModal: SyncOrgModal, confirm: confirmSyncOrg } =
    useConfirm({
      title: 'Synchronize Organization',
      message:
        'Please confirm that you want to start a synchronization for this organization to other systems like Logscale and Auth0.',
      button: 'Synchronize'
    });

  const { openDialog: openProductDialog, open: productDialogOpen } =
    useProductDialog();

  const organizationsQuickFilters = useMemo(() => {
    const qFilters = ORGANIZATIONS_QUICK_FILTERS.map((filter) => {
      let filterOptions = [];
      if (filter.field === 'product_type') {
        filterOptions = getProductTypeFilterOptions(organizations);
      } else if (filter.field === 'product_sub_type') {
        filterOptions = getProductSubTypeFilterOptions(organizations);
      } else {
        filterOptions = getSearchableSelectOptions(
          organizations,
          filter.field,
          filter.title
        );
      }
      return {
        ...filter,
        config: {
          ...filter.config,
          variant: filter.config.variant
        },
        filterOptions: [...filter.filterOptions, ...filterOptions]
      };
    });
    return qFilters;
  }, [organizations]);

  const handleContextMenuClick = async (
    action: string,
    id: string | number = 0
  ) => {
    const org = organizations.find((o) => o.id === +id);
    if (!org) return;
    if (action === 'Edit') {
      openDialog('Edit', org as OrganizationCreateRequest);
    } else if (action === 'Delete') {
      openDialog('Delete', org as OrganizationCreateRequest);
    } else if (action === 'Synchronize Organization') {
      const confirmed = await confirmSyncOrg();
      if (confirmed) {
        syncOrganization(org.id);
      }
    } else if (action === 'Clean Up Organization') {
      openDialog('CleanUp', org as OrganizationCreateRequest);
    }
    // Hide Add Product logic for now
    else if (action === 'Add Product') {
      openProductDialog('New', { ...EMPTY_PRODUCT, organization_id: org.id });
    }
  };

  return (
    <>
      <Header
        title="Organizations"
        actionsNewLine
        actions={
          <CheckPermissions permissions={PERMISSIONS.ORGANIZATIONS_CREATE}>
            <Button
              color="primary"
              startIcon={<FontAwesomeIcon icon={faPlus} />}
              variant="contained"
              onClick={() => openDialog('New')}
            >
              {isLayoutMobile ? 'New' : 'New Organization'}
            </Button>
            <Button
              color="primary"
              disabled={!isSyncAllowed()}
              variant="contained"
              onClick={() => openDialog('SalesforceSync')}
            >
              {isSyncAllowed() ? 'Sync Salesforce' : 'Salesforce Syncing...'}
            </Button>
            <ExportButton
              data={filteredOrganizations}
              fileName="pondurance-organizations"
              json
              csv
            />
          </CheckPermissions>
        }
      >
        <Box sx={{ px: 3, pb: 2 }}>
          <SearchBar
            className="px-8 pb-4"
            searchableSelectFilters={organizationsQuickFilters}
            selectedFilters={organizationsFilters}
            onFilterChange={setOrganizationsFilters}
          />
        </Box>
      </Header>
      <ContextMenuProvider
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        menuItems={ORGANIZATIONS_CONTEXT_MENU_ITEMS}
        clickHandler={handleContextMenuClick}
      >
        <MainContent>
          <LoadingIndicator isLoading={isFetching} />
          <div className={classes.organizationsPage}>
            <OrgTable />
          </div>
        </MainContent>
      </ContextMenuProvider>
      {open && <OrganizationEditDialog />}
      {productDialogOpen && <ProductEditDialog />}
      <SyncOrgModal />
    </>
  );
};

export default Organizations;
